import { ErrorMessage, Skeleton } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { type I18nInstance, useI18n } from '@affine/i18n';
import type { ListedBlobRecord } from '@affine/nbstore';
import { useService } from '@toeverything/infra';
import bytes from 'bytes';
import { useEffect, useMemo, useState } from 'react';

import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import { StorageFileGrid } from './storage-file-grid';
import * as styles from './style.css';

interface StorageBreakdownDocument {
  docId: string;
  title: string | null;
  trashed: boolean;
  snapshotBytes: number;
  historyBytes: number;
  outboxBytes: number;
  databaseBytes: number;
  blobBytes: number;
  totalBytes: number;
}

interface StorageBreakdownResponse {
  totalBytes: number;
  blobBytes: number;
  documentSnapshotBytes: number;
  documentHistoryBytes: number;
  databaseBytes: number;
  derivedBytes: number;
  searchIndexBytes: number;
  mailSearchIndexBytes: number;
  mailBodyCacheBytes: number;
  mailAttachmentCacheBytes: number;
  blobCount: number;
  calculatedAt: string | null;
  documents: StorageBreakdownDocument[];
  unmappedBlobBytes: number;
  unmappedBlobs: Array<{
    key: string;
    mime: string;
    size: number;
    createdAt: string | null;
  }>;
}

interface StorageBreakdownCategory {
  label: string;
  meta?: string;
  valueText: string;
}

const UNKNOWN_STORAGE_VALUE = '...';
const formatBytes = (value: number) =>
  bytes(value, { unitSeparator: ' ' }) ?? '0 B';
const SEARCH_INDEX_STORAGE_WARNING_SHARE = 0.2;
const WORKSPACE_STORAGE_I18N_PREFIX =
  'com.streamlain.settings.workspace.storage';

async function fetchStorageBreakdown(
  workspaceId: string,
  signal: AbortSignal
): Promise<StorageBreakdownResponse> {
  const response = await requestWorkspaceSetting(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/breakdown?limit=100`,
    {
      signal,
    }
  );
  if (!response.ok) {
    throw new Error(`Storage breakdown request failed: ${response.status}`);
  }
  return (await response.json()) as StorageBreakdownResponse;
}

function workspaceStorageText(
  i18n: I18nInstance,
  key: string,
  options?: Record<string, number | string>
): string {
  return i18n.t(`${WORKSPACE_STORAGE_I18N_PREFIX}.${key}`, options);
}

function formatShareOfTotal(
  i18n: I18nInstance,
  value: number,
  total: number
): string {
  const percent =
    !Number.isFinite(value) || !Number.isFinite(total) || total <= 0
      ? 0
      : Math.round((value / total) * 1000) / 10;
  return workspaceStorageText(i18n, 'shareOfTotal', {
    percent: String(percent),
  });
}

function formatSearchIndexStorageWarning(
  i18n: I18nInstance,
  data: StorageBreakdownResponse
): string | null {
  const indexBytes = data.searchIndexBytes + data.mailSearchIndexBytes;
  const mailCacheBytes =
    data.mailBodyCacheBytes + data.mailAttachmentCacheBytes;
  if (indexBytes <= 0 && mailCacheBytes <= 0) return null;
  const share = data.totalBytes > 0 ? indexBytes / data.totalBytes : 0;
  if (share >= SEARCH_INDEX_STORAGE_WARNING_SHARE) {
    return workspaceStorageText(i18n, 'searchIndexWarning.share', {
      share: formatShareOfTotal(i18n, indexBytes, data.totalBytes),
    });
  }
  return workspaceStorageText(i18n, 'searchIndexWarning.cache', {
    indexBytes: formatBytes(indexBytes),
    cacheBytes: formatBytes(mailCacheBytes),
  });
}

export const WorkspaceStorageBreakdownPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const [data, setData] = useState<StorageBreakdownResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!backendAvailable) {
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    setError(null);

    fetchStorageBreakdown(workspaceId, abortController.signal)
      .then(result => {
        setData(result);
      })
      .catch(err => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, [backendAvailable, workspaceId]);

  const categories = useMemo<StorageBreakdownCategory[]>(() => {
    const valueText = (value: number | undefined) =>
      data ? formatBytes(value ?? 0) : UNKNOWN_STORAGE_VALUE;
    return [
      {
        label: t['com.streamlain.settings.workspace.storage.breakdown.blobs'](),
        valueText: valueText(data?.blobBytes),
        meta: data
          ? t['com.streamlain.settings.workspace.storage.breakdown.blobs.meta'](
              { count: String(data.blobCount) }
            )
          : '',
      },
      {
        label:
          t['com.streamlain.settings.workspace.storage.breakdown.snapshots'](),
        valueText: valueText(data?.documentSnapshotBytes),
      },
      {
        label:
          t['com.streamlain.settings.workspace.storage.breakdown.history'](),
        valueText: valueText(data?.documentHistoryBytes),
      },
      {
        label:
          t['com.streamlain.settings.workspace.storage.breakdown.databases'](),
        valueText: valueText(data?.databaseBytes),
      },
      {
        label:
          t[
            'com.streamlain.settings.workspace.storage.breakdown.search-index'
          ](),
        valueText: valueText(data?.searchIndexBytes),
        meta: data
          ? formatShareOfTotal(t, data.searchIndexBytes, data.totalBytes)
          : '',
      },
      {
        label:
          t[
            'com.streamlain.settings.workspace.storage.breakdown.mail-search-index'
          ](),
        valueText: valueText(data?.mailSearchIndexBytes),
        meta: data
          ? formatShareOfTotal(t, data.mailSearchIndexBytes, data.totalBytes)
          : '',
      },
      {
        label:
          t[
            'com.streamlain.settings.workspace.storage.breakdown.mail-body-cache'
          ](),
        valueText: valueText(data?.mailBodyCacheBytes),
        meta: data
          ? formatShareOfTotal(t, data.mailBodyCacheBytes, data.totalBytes)
          : '',
      },
      {
        label:
          t[
            'com.streamlain.settings.workspace.storage.breakdown.mail-attachment-cache'
          ](),
        valueText: valueText(data?.mailAttachmentCacheBytes),
        meta: data
          ? formatShareOfTotal(
              t,
              data.mailAttachmentCacheBytes,
              data.totalBytes
            )
          : '',
      },
      {
        label:
          t['com.streamlain.settings.workspace.storage.breakdown.derived'](),
        valueText: valueText(data?.derivedBytes),
      },
    ];
  }, [data, t]);

  const backendUnavailable =
    !backendAvailable || (error !== null && isBackendUnavailableError(error));
  const searchIndexWarning = data
    ? formatSearchIndexStorageWarning(t, data)
    : null;
  const unmappedFiles = useMemo<ListedBlobRecord[]>(() => {
    return (
      data?.unmappedBlobs.map(blob => ({
        key: blob.key,
        mime: blob.mime,
        size: blob.size,
        createdAt: blob.createdAt ? new Date(blob.createdAt) : undefined,
      })) ?? []
    );
  }, [data?.unmappedBlobs]);

  if (loading && !data && !backendUnavailable) {
    return (
      <SettingRow
        name={t['com.streamlain.settings.workspace.storage.breakdown.title']()}
        desc=""
        spreadCol={false}
      >
        <Skeleton height={120} />
      </SettingRow>
    );
  }

  if (!data && !backendUnavailable) {
    return (
      <SettingRow
        name={t['com.streamlain.settings.workspace.storage.breakdown.title']()}
        desc=""
        spreadCol={false}
      >
        <ErrorMessage>
          {t['com.streamlain.settings.workspace.storage.breakdown.error']()}
        </ErrorMessage>
      </SettingRow>
    );
  }

  return (
    <SettingRow
      name={t['com.streamlain.settings.workspace.storage.breakdown.title']()}
      desc={t['com.streamlain.settings.workspace.storage.breakdown.desc']()}
      spreadCol={false}
    >
      <div className={styles.storageBreakdown}>
        {error && !backendUnavailable ? (
          <ErrorMessage>
            {t['com.streamlain.settings.workspace.storage.breakdown.error']()}
          </ErrorMessage>
        ) : null}
        <div className={styles.storageBreakdownSummary}>
          {categories.map(category => (
            <div className={styles.storageBreakdownMetric} key={category.label}>
              <div className={styles.storageBreakdownMetricLabel}>
                {category.label}
              </div>
              <div className={styles.storageBreakdownMetricValue}>
                {category.valueText}
              </div>
              <div className={styles.storageBreakdownMetricMeta}>
                {category.meta ?? ''}
              </div>
            </div>
          ))}
        </div>

        {searchIndexWarning ? (
          <div className={styles.storageBreakdownWarning}>
            {searchIndexWarning}
          </div>
        ) : null}

        <div className={styles.storageBreakdownMetaRow}>
          <span>
            {t['com.streamlain.settings.workspace.storage.breakdown.total']()}{' '}
            {data ? formatBytes(data.totalBytes) : UNKNOWN_STORAGE_VALUE}
          </span>
          {data?.calculatedAt ? (
            <span>
              {t[
                'com.streamlain.settings.workspace.storage.breakdown.calculated'
              ]()}{' '}
              {new Date(data.calculatedAt).toLocaleString()}
            </span>
          ) : null}
        </div>

        <div className={styles.storageBreakdownTableWrapper}>
          <table className={styles.storageBreakdownTable}>
            <thead>
              <tr>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.doc'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.total.short'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.snapshots'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.history'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.outbox'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.databases'
                  ]()}
                </th>
                <th>
                  {t[
                    'com.streamlain.settings.workspace.storage.breakdown.blobs'
                  ]()}
                </th>
              </tr>
            </thead>
            <tbody>
              {data ? (
                data.documents.map(doc => (
                  <tr key={doc.docId}>
                    <td className={styles.storageBreakdownDocCell}>
                      <span title={doc.docId}>
                        {doc.title ||
                          t[
                            'com.streamlain.settings.workspace.storage.breakdown.untitled'
                          ]()}
                      </span>
                      {doc.trashed ? (
                        <span className={styles.storageBreakdownBadge}>
                          {t[
                            'com.streamlain.settings.workspace.storage.breakdown.trashed'
                          ]()}
                        </span>
                      ) : null}
                    </td>
                    <td>{formatBytes(doc.totalBytes)}</td>
                    <td>{formatBytes(doc.snapshotBytes)}</td>
                    <td>{formatBytes(doc.historyBytes)}</td>
                    <td>{formatBytes(doc.outboxBytes)}</td>
                    <td>{formatBytes(doc.databaseBytes)}</td>
                    <td>{formatBytes(doc.blobBytes)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>{UNKNOWN_STORAGE_VALUE}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {unmappedFiles.length > 0 ? (
          <StorageFileGrid
            backendAvailable={backendAvailable}
            description={t.t(
              'com.streamlain.settings.workspace.storage.breakdown.unmapped-blobs.desc'
            )}
            emptyText=""
            files={unmappedFiles}
            title={t[
              'com.streamlain.settings.workspace.storage.breakdown.unmapped-blobs'
            ]()}
          />
        ) : null}
      </div>
    </SettingRow>
  );
};
